'use client'

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import DashboardContentWrapper from '../../../../shared/components/dashboard-content-wrapper'
import Heading from '../../../../shared/components/ui/typography/heading'
import { UrlSearchParams } from '../../../../shared/types'
import { resolveBaseLayoutConfig } from '@/features/base/utils/layout'
import { DEFAULT_BASE_LAYOUT_CONFIG } from '@/features/base/constants/default-layout-config'
import BaseTabs from '@/features/base/components/base-tabs'
import { SEARCH_PARAMS_TAB_KEY } from '@/features/base/constants/base-tab-links'
import { BaseLayoutConfig } from '@/features/base/types/base-layout'
import { BaseRegionResponse, unwrapBaseRegionResponse } from '@/features/base/types/region-response'
import { buildBaseLayoutContext } from '@/features/base/utils/context'
import { BaseSectionRenderer } from '@/features/base/components/base-section-renderer'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useRegion } from '@/shared/hooks/use-region'
import { Button } from '@/shared/components/ui/button'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useRegionAwareHref } from '@/shared/hooks/use-region-aware-href'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import { BasePhoto, MissionStory, PrayerRequest } from '@/features/base/types/base-content'
import { BASE_ABOUT_TAB_ID, BASE_ABOUT_TAB_LABEL, BASE_PHOTOS_TAB_ID, BASE_PRETRIP_TAB_ID } from '@/features/base/constants/base-tab-links'
import BasePhotoTimelineTab from '@/features/base/components/base-photo-timeline-tab'
import { PreTripTabContent } from '@/features/stm/components/group-detail/PreTripTabContent'
import { HubAboutTabContent, type HubAboutVideo } from '@/features/base/components/hub-about-tab-content'
import { PartnerRegionAssociation } from '@/features/partners/types/partner.type'
import LocationSelector from '@/shared/components/location-selector'
import ScrollToTopFab from '@/shared/components/scroll-to-top-fab'

function extractAssociations(raw: unknown): PartnerRegionAssociation[] {
  if (Array.isArray(raw)) {
    return raw as PartnerRegionAssociation[]
  }
  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>
    if (obj.data !== undefined) {
      return extractAssociations(obj.data)
    }
  }
  return []
}

function BasePage() {
  const { regionId, isLoading: isRegionLoading } = useRegionId()
  const { regionSlug } = useRegion()
  const { session } = useSession()
  const adminHref = useRegionAwareHref('/base/admin')
  const [selectedTabId, setSelectedTabId] = useState<string | undefined>(undefined)
  const [selectedRegionId, setSelectedRegionId] = useState<string | null>(null)
  const [regionData, setRegionData] = useState<BaseRegionResponse | null>(null)
  const [layoutConfig, setLayoutConfig] = useState<BaseLayoutConfig | null>(null)
  const [isLoadingRegionData, setIsLoadingRegionData] = useState(false)
  const [regionError, setRegionError] = useState<string | null>(null)
  const [photos, setPhotos] = useState<BasePhoto[]>([])
  const [missionStories, setMissionStories] = useState<MissionStory[]>([])
  const [prayerRequests, setPrayerRequests] = useState<PrayerRequest[]>([])
  const [partners, setPartners] = useState<PartnerRegionAssociation[]>([])
  const [aboutVideos, setAboutVideos] = useState<HubAboutVideo[]>([])
  const [isLoadingPhotos, setIsLoadingPhotos] = useState(false)
  const [isLoadingStories, setIsLoadingStories] = useState(false)
  const [isLoadingPrayerRequests, setIsLoadingPrayerRequests] = useState(false)
  const [isLoadingPartners, setIsLoadingPartners] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [missionStoriesError, setMissionStoriesError] = useState<string | null>(null)
  const [prayerRequestsError, setPrayerRequestsError] = useState<string | null>(null)
  const [partnersError, setPartnersError] = useState<string | null>(null)
  const [aboutVideosError, setAboutVideosError] = useState<string | null>(null)

  const searchParams = useSearchParams()

  useEffect(() => {
    const tabParam = searchParams.get(SEARCH_PARAMS_TAB_KEY)
    if (tabParam) {
      setSelectedTabId(tabParam)
    } else {
      setSelectedTabId(undefined)
    }
  }, [searchParams])

  const fetchRegion = useCallback(async () => {
    if (!regionId) {
      setRegionData(null)
      setLayoutConfig(null)
      return
    }

    try {
      setIsLoadingRegionData(true)
      setRegionError(null)
      // Use lightweight endpoint that only returns id, name, and baseLayoutConfig
      const response = await fetch(`/api/regions/${regionId}/base`)
      if (!response.ok) {
        throw new Error('Failed to fetch region data')
      }
      const payload = await response.json()
      const region = unwrapBaseRegionResponse(payload)
      if (!region) {
        throw new Error('Region data unavailable')
      }
      setRegionData(region)
      setLayoutConfig(resolveBaseLayoutConfig(region.baseLayoutConfig))
    } catch (error) {
      console.error('Error loading region data:', error)
      setRegionError('Unable to load region data')
      setRegionData(null)
      setLayoutConfig(null)
    } finally {
      setIsLoadingRegionData(false)
    }
  }, [regionId])

  useEffect(() => {
    if (isRegionLoading) return
    fetchRegion()
  }, [fetchRegion, isRegionLoading])

  const refreshPhotos = useCallback(async () => {
    if (!regionId) {
      setPhotos([])
      setPhotoError(null)
      return
    }
    try {
      setIsLoadingPhotos(true)
      setPhotoError(null)
      const response = await fetch(`/api/base-photos/region/${regionId}`)
      if (!response.ok) {
        throw new Error('Failed to load base photos')
      }
      const payload = await response.json()
      const nextPhotos: BasePhoto[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : payload?.data?.items ?? []
      setPhotos(nextPhotos)
    } catch (error) {
      console.error('Failed to load base photos', error)
      setPhotos([])
      setPhotoError('Failed to load base photos')
    } finally {
      setIsLoadingPhotos(false)
    }
  }, [regionId])

  const refreshMissionStories = useCallback(async () => {
    if (!regionId) {
      setMissionStories([])
      setMissionStoriesError(null)
      return
    }
    try {
      setIsLoadingStories(true)
      setMissionStoriesError(null)
      const response = await fetch(`/api/mission-stories?regionId=${regionId}`)
      if (!response.ok) {
        throw new Error('Failed to load mission stories')
      }
      const payload = await response.json()
      const nextStories: MissionStory[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : payload?.data?.items ?? []
      // Most recent first (by storyDate; fallback to createdAt/updatedAt)
      nextStories.sort((a, b) => {
        const dateA = a.storyDate || a.createdAt || a.updatedAt || ''
        const dateB = b.storyDate || b.createdAt || b.updatedAt || ''
        return dateB.localeCompare(dateA)
      })
      setMissionStories(nextStories)
    } catch (error) {
      console.error('Failed to load mission stories', error)
      setMissionStories([])
      setMissionStoriesError('Failed to load mission stories')
    } finally {
      setIsLoadingStories(false)
    }
  }, [regionId])

  const refreshPrayerRequests = useCallback(async () => {
    if (!regionId) {
      setPrayerRequests([])
      setPrayerRequestsError(null)
      return
    }
    try {
      setIsLoadingPrayerRequests(true)
      setPrayerRequestsError(null)
      const response = await fetch(`/api/prayer-requests?regionId=${regionId}`)
      if (!response.ok) {
        throw new Error('Failed to load prayer requests')
      }
      const payload = await response.json()
      const nextRequests: PrayerRequest[] = Array.isArray(payload?.data)
        ? payload.data
        : []
      setPrayerRequests(nextRequests)
    } catch (error) {
      console.error('Failed to load prayer requests', error)
      setPrayerRequests([])
      setPrayerRequestsError('Failed to load prayer requests')
    } finally {
      setIsLoadingPrayerRequests(false)
    }
  }, [regionId])

  const refreshPartners = useCallback(async () => {
    if (!regionId) {
      setPartners([])
      setPartnersError(null)
      return
    }
    try {
      setIsLoadingPartners(true)
      setPartnersError(null)
      const response = await fetch(`/api/organization-regions?regionId=${regionId}`)
      if (!response.ok) {
        throw new Error('Failed to load partners')
      }
      const raw = await response.json().catch(() => null)
      console.log('Base page - Partners API raw response:', raw)
      
      if (response.ok && raw) {
        const associations = extractAssociations(raw)
        console.log('Base page - Extracted associations:', associations.length)
        if (associations.length > 0) {
          console.log('Base page - Sample association:', {
            id: associations[0].id,
            isActive: associations[0].isActive,
            orgName: associations[0].organization?.name,
            hasGiveLink: !!associations[0].organization?.giveLink,
            giveLink: associations[0].organization?.giveLink,
            organizationKeys: associations[0].organization ? Object.keys(associations[0].organization) : [],
          })
        }
        setPartners(associations)
      } else {
        console.error('Base page - Failed to fetch partners:', response.status)
        setPartners([])
      }
    } catch (error) {
      console.error('Failed to load partners', error)
      setPartners([])
      setPartnersError('Failed to load partners')
    } finally {
      setIsLoadingPartners(false)
    }
  }, [regionId])

  const refreshAboutVideos = useCallback(async () => {
    if (!regionId) {
      setAboutVideos([])
      setAboutVideosError(null)
      return
    }
    try {
      setAboutVideosError(null)
      const response = await fetch(`/api/base-photos/region/${regionId}/about`)
      if (!response.ok) {
        throw new Error('Failed to load About videos')
      }
      const payload = await response.json()
      setAboutVideos(Array.isArray(payload?.data) ? payload.data : [])
    } catch (error) {
      console.error('Failed to load About videos', error)
      setAboutVideos([])
      setAboutVideosError('Failed to load About videos')
    }
  }, [regionId])

  useEffect(() => {
    refreshPhotos()
    refreshMissionStories()
    refreshPrayerRequests()
    refreshPartners()
    refreshAboutVideos()
  }, [refreshPhotos, refreshMissionStories, refreshPrayerRequests, refreshPartners, refreshAboutVideos])

  const tabs = useMemo(() => layoutConfig?.tabs ?? [], [layoutConfig])
  const isAdmin = session?.role === UserRole.ADMIN
  const canShowAboutTab = isAdmin || aboutVideos.length > 0
  const visibleTabs = useMemo(
    () => [
      ...tabs,
      ...(canShowAboutTab ? [{ id: BASE_ABOUT_TAB_ID, label: BASE_ABOUT_TAB_LABEL }] : []),
    ],
    [tabs, canShowAboutTab]
  )
  const activeTabId = useMemo(() => {
    if (selectedTabId) {
      if (selectedTabId === BASE_PHOTOS_TAB_ID) return BASE_PHOTOS_TAB_ID
      if (selectedTabId === BASE_PRETRIP_TAB_ID) return BASE_PRETRIP_TAB_ID
      if (selectedTabId === BASE_ABOUT_TAB_ID && canShowAboutTab) return BASE_ABOUT_TAB_ID
      const found = tabs.find((tab) => tab.id === selectedTabId)
      if (found) return found.id
    }
    return tabs[0]?.id ?? BASE_PHOTOS_TAB_ID
  }, [tabs, selectedTabId, canShowAboutTab])

  const activeTab = useMemo(() => {
    if (!activeTabId || activeTabId === BASE_PHOTOS_TAB_ID || activeTabId === BASE_PRETRIP_TAB_ID || activeTabId === BASE_ABOUT_TAB_ID) return undefined
    return tabs.find((tab) => tab.id === activeTabId)
  }, [tabs, activeTabId])

  const isBasePhotosTab = activeTabId === BASE_PHOTOS_TAB_ID
  const isPreTripTab = activeTabId === BASE_PRETRIP_TAB_ID
  const isAboutTab = activeTabId === BASE_ABOUT_TAB_ID

  const permissions = useMemo(
    () => ({
      canManagePhotos: session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER || session?.role === UserRole.TEAM_LEADER,
      canManageMissionStories: session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER,
      canDownloadPhotos: session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER,
    }),
    [session?.role],
  )

  const context = useMemo(() => {
    if (!regionData) return null
    return buildBaseLayoutContext(regionData, photos, missionStories, prayerRequests, partners, {
      permissions,
      meta: {
        regionId: regionId ?? null,
        currentUserId: session?.sub ?? null,
      },
      actions: {
        setPhotos,
        setMissionStories,
        refreshPhotos,
        refreshMissionStories,
      },
    })
  }, [regionData, photos, missionStories, prayerRequests, partners, permissions, regionId, session?.sub, refreshPhotos, refreshMissionStories])

  const isLoading = isRegionLoading || isLoadingRegionData || isLoadingPhotos || isLoadingStories || isLoadingPrayerRequests || isLoadingPartners
  const showAdminLink = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

  return (
    <DashboardContentWrapper className="flex flex-col gap-[15px] pb-24 md:pb-5">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <Heading>Hub</Heading>
        <LocationSelector />
      </div>

      {layoutConfig ? (
        <BaseTabs
          tabs={visibleTabs.map((tab) => ({ id: tab.id, label: tab.label }))}
          activeTab={activeTabId}
        />
      ) : (
        <div className="flex h-10 items-center gap-4">
          <div className="h-6 w-24 animate-pulse rounded bg-muted" />
          <div className="h-6 w-24 animate-pulse rounded bg-muted" />
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-10">
          <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        </div>
      )}

      {!isLoading && (regionError || photoError || missionStoriesError || prayerRequestsError || partnersError || (isAboutTab && aboutVideosError)) && (
        <div className="flex flex-col gap-2 py-10 text-center text-destructive">
          {regionError && <span>{regionError}</span>}
          {photoError && <span>{photoError}</span>}
          {missionStoriesError && <span>{missionStoriesError}</span>}
          {prayerRequestsError && <span>{prayerRequestsError}</span>}
          {partnersError && <span>{partnersError}</span>}
          {isAboutTab && aboutVideosError && <span>{aboutVideosError}</span>}
        </div>
      )}

      {!isLoading && !regionError && isBasePhotosTab && regionId && (
        <BasePhotoTimelineTab regionId={Number(regionId)} />
      )}

      {!isLoading && !regionError && isPreTripTab && regionId && (
        <PreTripTabContent
          regionId={Number(regionId)}
          regionSlug={regionSlug}
          showInstructionsCard={session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER}
          canEditPreTrip={session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER}
          compactVideoHeightOnDesktop
        />
      )}

      {!isLoading && !regionError && isAboutTab && regionId && (
        <HubAboutTabContent
          regionId={Number(regionId)}
          canEditAbout={isAdmin}
          allowHardDeleteAboutVideo={session?.role === UserRole.ADMIN}
        />
      )}

      {!isLoading && !regionError && context && activeTab && layoutConfig && !isBasePhotosTab && !isAboutTab && (
        <div className="space-y-6">
          {activeTab.sections.map((section) => (
            <BaseSectionRenderer key={section.id} section={section} context={context} />
          ))}
        </div>
      )}

      {!isLoading && !regionError && context && !activeTab && !isBasePhotosTab && !isPreTripTab && !isAboutTab && layoutConfig && (
        <div className="py-10 text-center text-muted-foreground">
          No sections available for this tab.
        </div>
      )}

      {!isLoading && !regionError && !context && (
        <div className="py-10 text-center text-muted-foreground">
          Select a region to view base information.
        </div>
      )}

      {showAdminLink && (
        <div className="flex justify-center pt-6">
          <Button asChild className="bg-primary hover:bg-primary/90 text-white font-bold">
            <Link href={adminHref}>Edit Hub Page</Link>
          </Button>
        </div>
      )}

      <ScrollToTopFab />
    </DashboardContentWrapper>
  )
}

export default BasePage
